import React from 'react';
import { Auth } from '@aws-amplify/auth';
import { Hub, HubCallback } from '@aws-amplify/core';

/**
 * The signed-in user's stable Cognito identity, or null when nobody is signed
 * in. Resolved from the locally stored session — no network — so it works in
 * the field with no connectivity. Returns `getUsername()` rather than the
 * `sub` attribute because reading `.attributes` triggers a Cognito call.
 *
 * This module is bootstrap plumbing for keying device state — the per-identity
 * persisted cache and offline-queue ownership — not for feature code. Screens
 * and business logic get the current user from `useAuthState()` in
 * `@/utils/auth`. See docs/ia/auth-and-identity.md.
 */
export const resolveCurrentIdentity = async (): Promise<string | null> => {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: false });
    return user?.getUsername() ?? null;
  } catch {
    return null;
  }
};

/**
 * Resolves the current Cognito identity on mount and re-resolves it on
 * Amplify Hub `signIn`/`signOut` events, so a login or logout inside a live
 * tab re-keys the persisted cache without a reload.
 *
 * Returns `undefined` while the identity is still resolving for the first
 * time, then `string | null` once resolved.
 */
export const useCurrentIdentity = (): string | null | undefined => {
  const [identity, setIdentity] = React.useState<string | null | undefined>(
    undefined
  );

  React.useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      const resolved = await resolveCurrentIdentity();
      if (!cancelled) setIdentity(resolved);
    };

    resolve();

    const listener: HubCallback = (data) => {
      switch (data.payload.event) {
        case 'signIn':
        case 'signOut':
        // Belt-and-braces: if configuration ever again lands after mount,
        // the hook re-resolves instead of stranding identity at null for
        // the session.
        case 'configured':
          resolve();
          break;
        default:
          break;
      }
    };
    const unsubscribe = Hub.listen('auth', listener);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return identity;
};
